import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Pencil, MoreHorizontal, Copy, Power, Trash2, History, AlertTriangle,
  Loader2, ShieldAlert,
} from "lucide-react";

/**
 * Global Master Record actions — Edit + More ▾ (Duplicate / Deactivate|Activate /
 * Delete / View History) with a dependency-guarded smart-delete flow and a
 * history viewer. Backed by /api/master-actions/{type}/{id}.
 *
 * Props: type ('customer'|'product'|'service'|'vendor'|'contact'), id, name,
 * active, onEdit (opens the module's own edit UI), onChanged (reload), onDuplicate?
 */
export function MasterActions({ type, id, name, active = true, onEdit, onChanged,
                               onDuplicate, testid = "master" }) {
  const [confirm, setConfirm] = useState(null); // 'delete' | 'deactivate' | 'activate'
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [deps, setDeps] = useState(null);
  const [depsLoading, setDepsLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [events, setEvents] = useState(null);

  const openDelete = async () => {
    setConfirm("delete"); setReason(""); setDeps(null); setDepsLoading(true);
    try {
      const { data } = await api.get(`/master-actions/${type}/${id}/dependencies`);
      setDeps(data);
    } catch { setDeps({ blocked: false, items: [], total: 0 }); }
    finally { setDepsLoading(false); }
  };

  const doDelete = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.delete(`/master-actions/${type}/${id}`, { params: { reason } });
      toast.success(`${name} deleted`);
      setConfirm(null); onChanged?.({ deleted: true });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Delete failed"));
    } finally { setBusy(false); }
  };

  const doDeactivate = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/master-actions/${type}/${id}/deactivate`, { reason });
      toast.success(`${name} deactivated`);
      setConfirm(null); onChanged?.({ active: false });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doActivate = async () => {
    setBusy(true);
    try {
      await api.post(`/master-actions/${type}/${id}/activate`, { reason: reason || null });
      toast.success(`${name} reactivated`);
      setConfirm(null); onChanged?.({ active: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openHistory = async () => {
    setHistoryOpen(true); setEvents(null);
    try {
      const { data } = await api.get(`/master-actions/${type}/${id}/history`);
      setEvents(data.events || []);
    } catch { setEvents([]); }
  };

  return (
    <>
      <div className="flex items-center gap-2" data-testid={`${testid}-actions`}>
        {onEdit && (
          <Button variant="outline" onClick={onEdit} data-testid={`${testid}-edit-btn`}>
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid={`${testid}-more-btn`}>
              <MoreHorizontal className="h-4 w-4" /> <span className="ml-1">More</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {onDuplicate && (
              <DropdownMenuItem onClick={onDuplicate} data-testid={`${testid}-duplicate`}>
                <Copy className="h-4 w-4 mr-2" /> Duplicate
              </DropdownMenuItem>
            )}
            {active ? (
              <DropdownMenuItem onClick={() => { setConfirm("deactivate"); setReason(""); }}
                data-testid={`${testid}-deactivate`}>
                <Power className="h-4 w-4 mr-2" /> Deactivate
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => { setConfirm("activate"); setReason(""); }}
                data-testid={`${testid}-activate`}>
                <Power className="h-4 w-4 mr-2 text-emerald-600" /> Reactivate
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={openHistory} data-testid={`${testid}-history`}>
              <History className="h-4 w-4 mr-2" /> View History
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={openDelete}
              className="text-rose-600 focus:text-rose-700" data-testid={`${testid}-delete`}>
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Delete / smart-delete */}
      <Dialog open={confirm === "delete"} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid={`${testid}-delete-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600" /> Delete record?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div><span className="text-slate-500">Record:</span> <span className="font-medium">{name}</span>
              <div className="text-[11px] text-slate-400 font-mono">{id}</div></div>
            {depsLoading && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Checking dependencies…</div>}
            {deps && deps.blocked && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid={`${testid}-delete-blocked`}>
                <div className="flex items-center gap-1.5 font-semibold text-amber-800 text-[13px]">
                  <AlertTriangle className="h-4 w-4" /> Delete not available
                </div>
                <p className="text-[12px] text-amber-700 mt-1">This record has existing business history and cannot be permanently deleted.</p>
                <ul className="text-[12px] text-amber-800 mt-2 space-y-0.5">
                  {deps.items.map((d) => <li key={d.label}>• {d.count} {d.label}</li>)}
                </ul>
                <Button className="mt-3 w-full bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => { setConfirm("deactivate"); setReason(""); }}
                  data-testid={`${testid}-delete-to-deactivate`}>
                  <Power className="h-4 w-4 mr-1.5" /> Deactivate instead
                </Button>
              </div>
            )}
            {deps && !deps.blocked && (
              <>
                <div className="text-emerald-700 text-[12px]">No linked business records — this record can be permanently deleted.</div>
                <div>
                  <Label>Reason (required)</Label>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is this being deleted?" data-testid={`${testid}-delete-reason`} />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            {deps && !deps.blocked && (
              <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doDelete}
                disabled={busy} data-testid={`${testid}-delete-confirm`}>
                {busy ? "Deleting…" : "Delete permanently"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate */}
      <Dialog open={confirm === "deactivate"} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid={`${testid}-deactivate-dialog`}>
          <DialogHeader><DialogTitle>Deactivate {name}?</DialogTitle></DialogHeader>
          <div className="text-sm space-y-3">
            <p className="text-slate-600 text-[13px]">The record stays searchable and all history is kept, but it won't appear in normal active selection or new transactions.</p>
            <div>
              <Label>Reason (required)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Duplicate / no longer trading" data-testid={`${testid}-deactivate-reason`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={doDeactivate}
              disabled={busy} data-testid={`${testid}-deactivate-confirm`}>
              {busy ? "Working…" : "Deactivate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate */}
      <Dialog open={confirm === "activate"} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid={`${testid}-activate-dialog`}>
          <DialogHeader><DialogTitle>Reactivate {name}?</DialogTitle></DialogHeader>
          <p className="text-slate-600 text-[13px]">This record will be available for selection and new transactions again.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={doActivate}
              disabled={busy} data-testid={`${testid}-activate-confirm`}>
              {busy ? "Working…" : "Reactivate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid={`${testid}-history-dialog`}>
          <DialogHeader><DialogTitle>History — {name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {events === null && <div className="flex items-center gap-2 text-slate-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
            {events && events.length === 0 && <div className="text-slate-400 text-sm py-6 text-center">No history recorded.</div>}
            {events && events.map((e, i) => (
              <div key={i} className="border-b border-slate-100 py-2 text-[12px]" data-testid={`${testid}-history-event-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{(e.action || "").replace(/^master\./, "").replace(/\./g, " ")}</span>
                  <span className="text-slate-400">{(e.at || "").toString().slice(0, 19).replace("T", " ")}</span>
                  <span className="ml-auto text-slate-500">{e.by}</span>
                </div>
                {e.reason && <div className="text-slate-500 italic">Reason: {e.reason}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MasterActions;
